import type { JSX } from 'react';
import { Code2, Droplets, Target } from 'lucide-react';
import { Container } from '@components/ui/Container';
import { RevealOnView } from '@components/ui/RevealOnView';
import { SectionHeading } from '@components/ui/SectionHeading';
import { landingCopy } from '@features/landing/data/landing.pt';
import type { QuemSomosParagraph } from '@features/landing/data/types';

const ICON_BY_KEY: Record<QuemSomosParagraph['iconKey'], JSX.Element> = {
  origin: <Droplets className="h-7 w-7" aria-hidden="true" />,
  purpose: <Target className="h-7 w-7" aria-hidden="true" />,
  founder: <Code2 className="h-7 w-7" aria-hidden="true" />,
};

const STAGGER_DELAY_MS = 120;

/**
 * Secao "Quem somos" (SECT-02 — PRD §4.3).
 *
 * Apresenta 3 paragrafos sobre origem do nome, proposito e quem esta por tras
 * da Omanna. Estrutura visual derivada da peca de divulgacao do Lucas
 * (.planning/design-reference.md): cada paragrafo e uma "row" com:
 *  - Circle icon a esquerda (bg-brand-100, ~64px, lucide icon em brand-700)
 *  - Bold lead a direita (font-display, neutral-800) — 1 linha
 *  - Body abaixo do lead (font-sans neutral-700) — paragrafo completo do PRD
 *
 * Anchor: `#quem-somos` (usado pelo menu Sobre do Navbar).
 * Background: `bg-white` para criar contraste com o Hero (`bg-neutral-50`),
 * dando ritmo visual entre secoes (mesma intencao do reference do Lucas).
 *
 * Reveal: cada row revela com stagger de 120ms via RevealOnView (CSS-only
 * scroll reveal via IntersectionObserver — UTIL-02). Conteudo sempre no DOM,
 * acessivel a leitores de tela desde o primeiro paint.
 *
 * Acessibilidade:
 *  - `<section id="quem-somos" aria-labelledby="quem-somos-heading">`
 *  - SectionHeading renderiza um `<h2 id="quem-somos-heading">`
 *  - Icons sao decorativos (aria-hidden) — o lead serve como rotulo semantico
 */
export const QuemSomos = (): JSX.Element => {
  const { quemSomos } = landingCopy;

  return (
    <section
      id="quem-somos"
      aria-labelledby="quem-somos-heading"
      className="bg-white py-20 lg:py-28"
    >
      <Container>
        <RevealOnView>
          <SectionHeading
            as="h2"
            eyebrow={quemSomos.eyebrow}
            title={quemSomos.title}
            align="left"
            className="max-w-3xl"
            titleId="quem-somos-heading"
          />
        </RevealOnView>

        <div className="mt-14 grid gap-10 lg:gap-12">
          {quemSomos.paragraphs.map((paragraph, index) => (
            <RevealOnView
              key={paragraph.iconKey}
              delayMs={STAGGER_DELAY_MS * (index + 1)}
              className="grid grid-cols-[auto_1fr] items-start gap-5 sm:gap-7"
            >
              <div className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                {ICON_BY_KEY[paragraph.iconKey]}
              </div>
              <div>
                <p className="font-display text-xl font-bold text-neutral-800 sm:text-2xl">
                  {paragraph.lead}
                </p>
                <p className="mt-3 max-w-3xl text-base leading-relaxed text-neutral-700">
                  {paragraph.body}
                </p>
              </div>
            </RevealOnView>
          ))}
        </div>
      </Container>
    </section>
  );
};
